import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as NavigationArrowIcon } from '../../../../assets/icons/navigate-back.svg';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  setEndConsumerData,
  setSelectedEndConsumer,
} from '../../../../redux/slices/EndConsumerSlice';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import TabToggle from '../../../../shared-components/tab-toggle/TabToggle';
import TablesToggle from '../../../../shared-components/tables-toggle/TablesToggle';
import endConsumerApi from '../../../../utils/api/end-consumer-v2';
import SessionsCountChart from '../ChartComponents/ProfileChart/SessionsCountChart';
import SpendChart from '../ChartComponents/ProfileChart/SpendChart';
import TotalConsumptionChart from '../ChartComponents/ProfileChart/TotalConsumptionChart';

import ChargeSessionCommercialList from './ChargeSessionCommercialList';
import ChargeSessionPersonalList from './ChargeSessionPersonalList';
import GuestChargingList from './GuestChargingList';
import ProfileConsumer from './ProfileConsumer';
import ReservationsList from './ReservationsList';
import ScheduleCompletedList from './ScheduleCompletedList';
import ScheduleUpcomingList from './ScheduleUpcomingList';
import TransactionHistoryList from './TransactionHistoryList';

interface NotificationItem {
  title: string;
  createdAt: string;
}

interface OtpItemInterface {
  createdAt: string;
  otp: string;
  otpExpiresAt: string;
  type: string;
  updatedAt: string;
  userId: string;
}

/**
 * EndConsumers Profile
 * @returns
 */
function EndConsumersProfile() {
  const { t, i18n } = useTranslation('endConsumer');
  const { selectedEndConsumer } = useAppSelector((state) => state.endConsumerType);
  const dispatch = useAppDispatch();
  const { type } = useParams<{ type: string }>();
  const { userCustomId } = useParams<{ userCustomId: string }>();
  const navigate = useNavigate();
  const [openTabList, setOpenTablist] = React.useState<number>(0);
  const { pathname } = useLocation();
  const [openTab, setOpenTab] = React.useState<number>(0);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [otps, setOtps] = useState<OtpItemInterface[]>([]);
  const [menuTab, setMenuTab] = React.useState<number>(0);

  /**
   * Change tab + update URL
   * @param tabLabel
   */
  const changeTabPages = (tabLabel: string) => {
    const index = [t('totalConsumption'), t('spend'), t('sessionsCount')].indexOf(tabLabel);
    setOpenTab(index);
  };

  /**
   * Change tab + update URL
   * @param tabLabel
   */
  const changeTabPagesList = (tabLabel: string) => {
    const index = [
      t('chargeSession'),
      t('schedule'),
      t('reservations'),
      t('guestCharging'),
      t('transactionHistory'),
    ].indexOf(tabLabel);
    setOpenTablist(index);
  };

  /**
   * Change tab + update URL
   * @param {number} tabLabel
   */
  const handleMenuTabChange = (tabLabel: string) => {
    const index = [t('notifications'), t('otps')].indexOf(tabLabel);
    setMenuTab(index);
  };

  /**
   * Fetching card list api
   */
  const handleNotificationsCard = async (consumerId: string) => {
    try {
      const response = await endConsumerApi.fetchNotifications(consumerId);

      // IMPORTANT: pick only notifications array
      setNotifications(response?.data?.results || []);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || 'Something Went Wrong',
          status: error?.response?.status || 500,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Fetching otp list api
   */
  const handleFetchOtp = async (consumerId: string) => {
    try {
      const response = await endConsumerApi.fetchOtps(consumerId);
      console.log(response);

      // IMPORTANT: pick only notifications array
      setOtps(response?.data || []);
    } catch (error: any) {
      console.error(error);
    }
  };

  /**
   * Navigate back to the previous page
   */
  const handleNavigateBack = () => {
    navigate('../');
  };

  useEffect(() => {
    // Run only for profile page
    if (!pathname.includes('/end-consumer-v2/profile')) return;

    requestAnimationFrame(() => {
      const scrollContainer = document.querySelector('.ev-content');

      if (scrollContainer instanceof HTMLElement) {
        scrollContainer.scrollTop = 0;
      } else {
        window.scrollTo(0, 0);
      }
    });
  }, []);

  useEffect(() => {
    dispatch(
      setSelectedEndConsumer({
        userCustomId,
      }),
    );
    dispatch(
      setEndConsumerData({
        name: 'endConsumerType',
        value: type || '',
      }),
    );
  }, [userCustomId, type]);

  useEffect(() => {
    if (selectedEndConsumer?.userCustomId) {
      handleNotificationsCard(selectedEndConsumer?.userCustomId);
      handleFetchOtp(selectedEndConsumer?.userCustomId);
    }
  }, [selectedEndConsumer?.userCustomId]);
  return (
    <div className="end-consumers-container" dir={i18n.dir()}>
      <div className="end-consumer-profile-header">
        <div className="navigation-arrow" role="presentation" onClick={handleNavigateBack}>
          <NavigationArrowIcon className="icon" />
        </div>
        <p>{t('endConsumerProfile')}</p>
      </div>
      <div className="end-consumers-pro">
        <div className="profile-consumer">
          <ProfileConsumer />
        </div>
        <div className="main-content">
          <div className="chart-notification">
            <div className="chart-content">
              <div>
                <TabToggle
                  tabs={[t('totalConsumption'), t('spend'), t('sessionsCount')]}
                  defaultTab={t('totalConsumption')}
                  onChange={(tab) => changeTabPages(tab)}
                />
              </div>
              <div className="table-content">
                {openTab === 0 && (
                  <div>
                    <TotalConsumptionChart />
                  </div>
                )}
                {openTab === 1 && (
                  <div>
                    <SpendChart />
                  </div>
                )}
                {openTab === 2 && (
                  <div>
                    <SessionsCountChart />
                  </div>
                )}
              </div>
            </div>

            <div className="menu-tab-wrapper">
              <TabToggle
                tabs={[t('notifications'), t('otps')]}
                defaultTab={t('notifications')}
                onChange={(tab) => handleMenuTabChange(tab)}
              />
              {menuTab === 0 && (
                <div className="menu-tab-container">
                  <div className="menu-tab-body">
                    {notifications?.length > 0 ? (
                      notifications.map((item: NotificationItem, index: number) => (
                        <div key={index} className="menu-tab-row">
                          <span className="title">{item.title}</span>
                          <span className="time">{new Date(item.createdAt).toLocaleString()}</span>
                        </div>
                      ))
                    ) : (
                      <p className="no-data">{t('noNotificationsFound')}</p>
                    )}
                  </div>
                </div>
              )}
              {menuTab === 1 && (
                <div className="menu-tab-container">
                  <div className="menu-tab-body">
                    {otps?.length > 0 ? (
                      otps.map((item: OtpItemInterface, index: number) => (
                        <div key={index} className="menu-tab-row">
                          <span className="title">{item.otp}</span>
                          <span className="time">{new Date(item.createdAt).toLocaleString()}</span>
                        </div>
                      ))
                    ) : (
                      <p className="no-data">{t('noOtpsFound')}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="table-list">
            <TabToggle
              tabs={[
                t('chargeSession'),
                t('schedule'),
                t('reservations'),
                t('guestCharging'),
                t('transactionHistory'),
              ]}
              defaultTab={t('chargeSession')}
              onChange={(tab) => changeTabPagesList(tab)}
            />
            <div className="table-content">
              {openTabList === 0 && (
                <div>
                  <TablesToggle
                    tabs={[
                      { label: t('commercial'), content: <ChargeSessionCommercialList /> },
                      { label: t('personal'), content: <ChargeSessionPersonalList /> },
                    ]}
                  />{' '}
                </div>
              )}
              {openTabList === 1 && (
                <div>
                  <TablesToggle
                    tabs={[
                      { label: t('upcoming'), content: <ScheduleUpcomingList /> },
                      { label: t('completed'), content: <ScheduleCompletedList /> },
                    ]}
                  />
                </div>
              )}
              {openTabList === 2 && (
                <div>
                  <ReservationsList />
                </div>
              )}
              {openTabList === 3 && (
                <div>
                  <GuestChargingList />
                </div>
              )}
              {openTabList === 4 && (
                <div>
                  <TransactionHistoryList />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EndConsumersProfile;
